'use client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faRefresh
} from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
    flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import React, { useState, useEffect, useRef } from 'react';
import { Filter } from 'src/components/era-package/filters';
import ChildCard from 'src/components/era-package/shared/ChildCard';
import { useColumns } from './hooks';
import { DetailModal } from '../DetailModal';
import { useServerPagination } from '../../hooks/useServerPagination';
import AbiEntrySummaryApiUrls from '../../../../apiUrls/AbiEntrySummaryApiUrls';
import pathMap from 'src/paths/pathMap';
import { Link } from 'react-router-dom';

const COLUMN_TO_PARAM = {
  entryNumber: 'entryNumber',
  referenceNo: 'referenceNo',
  bl: 'bl',
  '3461Status': 'status3461',
  '7501Status': 'status7501',
  entryPort: 'entryPort',
  importer: 'importer',
  consignee: 'consignee',
  user: 'user',
  '1usg': 'oneUSG',
};

const ENTRY_LIST_DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  entryNumber: '',
  referenceNo: '',
  status7501: '',
  status3461: '',
  entryPort: '',
  importer: '',
  consignee: '',
  user: '',
  bl: '',
  oneUSG: '',
};

// Main Component
const ABISubmissions = () => {
  const [searchInput, setSearchInput] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [columnVisibility, setColumnVisibility] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleToggleExpand = (abiTransId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(abiTransId) ? next.delete(abiTransId) : next.add(abiTransId);
      return next;
    });
  };

    const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } = useServerPagination(AbiEntrySummaryApiUrls.GET_ENTRY_LIST, ENTRY_LIST_DEFAULT_PARAMS);

  const columns = useColumns({ expandedRows, onToggleExpand: handleToggleExpand });
  const debounceRef = useRef(null);

  


  const handleRowClick = row => {
    setSelectedRow(row.original)
  }

    const pageIndex = params.page;
  const pageSize = params.size;

    const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      columnVisibility,
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
    pageCount: totalPages,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(next);
      const mapped = {};
      Object.values(COLUMN_TO_PARAM).forEach((f) => (mapped[f] = ''));
      next.forEach(({ id, value }) => {
        const key = COLUMN_TO_PARAM[id];
        if (key) mapped[key] = value ?? '';
      });
      updateParams({ ...mapped, page: 0 });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const handleDownload = () => {
    const headers = columns.map((col) => col.header());
    const rows = data.map((item) => [
      item.entryNumber,
      item.referenceNo,
      item.bl,
      item['3461Status'],
      item['7501Status'],
      item.pga,
      item['1c'],
      item['1usg'],
      item.entryPort,
      item.eta,
      item.statementDate,
      item.importer,
      item.consignee,
      item.user,
      item.lastModified,
    ]);

    const csvContent = [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'abi-submissions.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

    return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        {/* Search and Filter Controls */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search all columns..."
                                  value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              clearTimeout(debounceRef.current);
              debounceRef.current = setTimeout(() => {
                updateParams({ search: value, page: 0 });
              }, 500);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16, height: 16 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: { xs: '100%', sm: '300px' },
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                inset: 0
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
          >
            {showFilters ? 'Hide Filters' : 'Show Column Filters'}
          </Button>
          {/* remove download btn
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<FontAwesomeIcon icon={faDownload} style={{ width: 16, height: 16 }} />}
          >
            Download Data
          </Button> */}
          <Button
            component={Link}
            to={pathMap.ABI_ENTRY_SUMMARY_ADD_VIEW}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} style={{ width: 16, height: 16 }} />}
          >
            Create Entry Summary
          </Button>
        </Stack>
        <ChildCard sx={{ p: 0, mb: -3 }}>
          <Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <React.Fragment key={headerGroup.id}>
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={header.id}>
                            <Typography
                              variant="body"
                              color={'primary.main'}
                              fontWeight="600"
                              {...{
                                className: header.column.getCanSort()
                                  ? 'cursor-pointer select-none'
                                  : '',
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted()] ?? null}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                      {showFilters && (
                        <TableRow>
                          {headerGroup.headers.map((header) => (
                            <TableCell key={header.id}>
                              {header.column.getCanFilter() && showFilters ? (
                                <div>
                                  <Filter column={header.column} />
                                </div>
                              ) : null}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <Typography variant="body2" color="text.secondary">No submissions data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {table.getRowModel().rows.map((row) => {
                    const pga = row.original.pga;
                    const isExpanded = expandedRows.has(row.original.abiTransId);
                    return (
                    <React.Fragment key={row.id}>
                      <TableRow sx={{
                        cursor: 'pointer',
                        transition: theme => theme.transitions.create(),
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                        onClick={() => handleRowClick(row)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} sx={cell.column.columnDef.meta?.style}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                      {pga && pga.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={columns.length} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} unmountOnExit>
                              <Box sx={{ pl: 6, pr: 2, py: 1, backgroundColor: 'action.hover' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><Typography variant="caption" fontWeight={600}>Line item</Typography></TableCell>
                                      <TableCell><Typography variant="caption" fontWeight={600}>Entry number</Typography></TableCell>
                                      <TableCell><Typography variant="caption" fontWeight={600}>Description</Typography></TableCell>
                                      <TableCell><Typography variant="caption" fontWeight={600}>Agency</Typography></TableCell>
                                      <TableCell><Typography variant="caption" fontWeight={600}>Program</Typography></TableCell>
                                      <TableCell><Typography variant="caption" fontWeight={600}>Status</Typography></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {pga.map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell><Typography variant="body2">{item.line}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{item.entryNo}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{item.description}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{item.agency}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{item.program}</Typography></TableCell>
                                        <TableCell><Typography variant="body2">{item.status}</Typography></TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Stack
              gap={1}
              p={3}
              alignItems="center"
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1}>
                          <Button variant="contained" color="primary" onClick={() => fetchPage()}
                  startIcon={<FontAwesomeIcon icon={faRefresh} style={{ width: 16, height: 16 }} spin={loading} />}
                >
                  Refresh data
                </Button>
                <Typography variant="body1">
                  {totalElements} Rows
                </Typography>
              </Box>
              <Box
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'flex',
                  },
                }}
                alignItems="center"
                gap={1}
              >
                                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body1">Page</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {pageIndex + 1} of {totalPages}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  | Go to page:
                                    <TextField
                    type="number"
                    inputProps={{ min: 1, max: totalPages }}
                    defaultValue={pageIndex + 1}
                                                            onChange={(e) => {
                      const val = Number(e.target.value);
                      if (!val || val < 1) return;
                      updateParams({ page: Math.min(val, totalPages) - 1 });
                    }}
                    size="small"
                  />
                </Stack>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    updateParams({ size: Number(e.target.value), page: 0 });
                  }}
                  size="small"
                >
                  {[5, 10, 15, 20, 25].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  size="small"
                  onClick={() => updateParams({ page: 0 })}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => updateParams({ page: pageIndex - 1 })}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => updateParams({ page: pageIndex + 1 })}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => updateParams({ page: totalPages - 1 })}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        </ChildCard>
      </Stack>

      <DetailModal id={selectedRow?.abiTransId}
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        fetchData={() => fetchPage()}
        rowData={selectedRow}
      />
    </LocalizationProvider>
  );
};

export default ABISubmissions;
